import { faBars, faBullhorn, faHouse, faImages, faImage } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, usePage } from "@inertiajs/react";
import { useScreenSize } from "../../../hooks/useScreenSize";

const MAX_VISIBLE_ITEMS = 4;

interface MenuItem {
  id: string;
  label: string;
  link: string;
  children: MenuItem[];
  icon?: any;
}

function MenuItemComponent({ item }: { item: MenuItem }) {
  const { url } = usePage();
  const hasChildren = item.children && item.children.length > 0;
  const isActive = url === item.link || url.startsWith(item.link + '/');

  if (hasChildren) {
    return (
      <li>
        <details>
          <summary>
            <Link href={item.link} className={isActive ? 'menu-active' : ''}>
              {item.icon && <FontAwesomeIcon icon={item.icon} />}
              {item.label}
            </Link>
          </summary>
          <ul>
            {item.children.map((child) => (
              <MenuItemComponent key={child.id} item={child} />
            ))}
          </ul>
        </details>
      </li>
    );
  }

  return (
    <li>
      <Link href={item.link} className={isActive ? 'menu-active' : ''}>
        {item.icon && <FontAwesomeIcon icon={item.icon} />}
        {item.label}
      </Link>
    </li>
  );
}

export interface NavigationData {
  id: number;
  name: string;
  url: string;
  full_path: string;
  item_type: 'NAV' | 'PAGE' | 'MENU';
  external_link?: string;
  label?: string;
  children: NavigationData[];
  pages: PageData[];
}

interface PageData {
  id: number;
  title: string;
  slug: string;
  full_path: string;
}

export default function NavigationBar({
  referencesSections
}: any) {
  const { screenLargerThan } = useScreenSize();
  const isLarge = screenLargerThan('lg');

  return (
    <div style={{
      height: '70px',
      width: '100%',
      backgroundColor: 'var(--color-background)',
      display: 'flex',
      flexDirection: 'row',
      flexShrink: 0,
      zIndex: 999,
    }}>
      <div className="navbar bg-base-100 shadow-sm">
        <div className="navbar-start">
          <Link href="/">
            <img
              src="/logo.png"
              className={isLarge ? "h-15" : "h-12"}
              alt="Logo"
            />
          </Link>
        </div>
        {isLarge && (
          <div className="navbar-center" style={{
            border: '1px solid var(--color-neutral)',
          }}>
            <ul className="menu menu-horizontal bg-base-300 px-2">
              <MenuItemComponent
                item={{
                  id: 'home',
                  label: 'Home',
                  link: '/',
                  children: [],
                  icon: faHouse
                }}
              />
              <MenuItemComponent
                item={{
                  id: 'announcements',
                  label: 'Annonces',
                  link: '/announcements',
                  children: [],
                  icon: faBullhorn
                }}
              />
              <MenuItemComponent
                item={{
                  id: 'media',
                  label: 'Médias',
                  link: '/media',
                  children: [],
                  icon: faImages
                }}
              />
              <MenuItemComponent
                item={{
                  id: 'icons',
                  label: 'Icônes',
                  link: '/icons',
                  children: [],
                  icon: faImage
                }}
              />
              {referencesSections && referencesSections.slice(0, MAX_VISIBLE_ITEMS).map((section: any) => (
                <li key={section.id}>
                  {section.references && section.references.length > 0 ? (
                    <details>
                      <summary>
                        <Link href={`/references/${section.id}`} onClick={(e) => e.stopPropagation()}>
                          {section.title}
                        </Link>
                      </summary>
                      <ul className="menu menu-sm dropdown-content bg-base-300 z-[1000] mt-3 w-52 p-2 shadow rounded-none border border-base-content/20">
                        {section.references.map((reference: any) => (
                          <li key={reference.id}>
                            <Link href={reference.path}>{reference.title}</Link>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : (
                    <Link href={`/references/${section.id}`}>{section.title}</Link>
                  )}
                </li>
              ))}
              {referencesSections && referencesSections.length > MAX_VISIBLE_ITEMS && (
                <li>
                  <details>
                    <summary>
                      More +{referencesSections.length - MAX_VISIBLE_ITEMS}
                    </summary>
                    <ul className="menu menu-sm dropdown-content bg-base-300 z-[1000] mt-3 w-64 p-2 shadow rounded-none border border-base-content/20">
                      {referencesSections.slice(MAX_VISIBLE_ITEMS).map((section: any) => (
                        <li key={section.id}>
                          {section.references && section.references.length > 0 ? (
                            <details>
                              <summary>
                                <Link href={`/references/${section.id}`} className="font-semibold" onClick={(e) => e.stopPropagation()}>
                                  {section.title}
                                </Link>
                              </summary>
                              <ul className="menu menu-sm">
                                {section.references.map((reference: any) => (
                                  <li key={reference.id}>
                                    <Link href={reference.path}>{reference.title}</Link>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          ) : (
                            <Link href={`/references/${section.id}`} className="font-semibold">{section.title}</Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                </li>
              )}
            </ul>
          </div>
        )}
        <div className="navbar-end flex gap-2">
          {/* {isSmall && (
            <button className="btn btn-sm btn-primary">
              Contact Us
            </button>
          )} */}
          {!isLarge && (
            <div className="dropdown dropdown-end">
              <div tabIndex={0} role="button" className="btn btn-sm btn-ghost" style={{
                border: '1px solid var(--color-neutral)',
              }}>
                Menu
                <FontAwesomeIcon icon={faBars} className="h-5 w-5" />
              </div>
              <ul
                tabIndex={0}
                className="menu menu-sm dropdown-content bg-base-300 z-[1000] mt-3 w-52 p-2 shadow"
                style={{
                  border: '1px solid var(--color-neutral)',
                }}
              >
                <li>
                  <Link href="/">
                    <FontAwesomeIcon icon={faHouse} />
                    Home
                  </Link>
                </li>
                <li>
                  <Link href="/announcements">
                    <FontAwesomeIcon icon={faBullhorn} />
                    Annonces
                  </Link>
                </li>
                <li>
                  <Link href="/media">
                    <FontAwesomeIcon icon={faImages} />
                    Médias
                  </Link>
                </li>
                <li>
                  <Link href="/icons">
                    <FontAwesomeIcon icon={faImage} />
                    Icônes
                  </Link>
                </li>
                {referencesSections && referencesSections.map((section: any) => (
                  <li key={section.id}>
                    {section.references && section.references.length > 0 ? (
                      <details>
                        <summary>
                          <Link href={`/references/${section.id}`} onClick={(e) => e.stopPropagation()}>
                            {section.title}
                          </Link>
                        </summary>
                        <ul className="menu menu-sm">
                          {section.references.map((reference: any) => (
                            <li key={reference.id}>
                              <Link href={reference.path}>{reference.title}</Link>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : (
                      <Link href={`/references/${section.id}`}>{section.title}</Link>
                    )}
                  </li>
                ))}
                {/* {!isSmall && (
                  <li>
                    <button className="btn btn-sm btn-primary w-full">
                      Contact Us
                    </button>
                  </li>
                )} */}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
